import type { BuildingGraph, BuildingGraphPlane } from "building-types/building/buildingGraphTypes";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Framing, ResolvedOpeningFramingTable, WallFramingMeta } from "building-types/framing/wallFramingTypes";
import { OpeningSource } from "building-types/framing/wallFramingTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallPlaneDescriptor, WallPlaneSubdomain } from "building-types/walls2/wallPlane";
import type { WallPlaneSides } from "building-types/walls/wallPlaneSideTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { planeProject } from "geom/plane";
import { polyLine3EndPoint, polyLine3StartPoint } from "geom/polyLine3";
import {
  adjacentRoomHandlesFromWallPlaneSides,
  resolveWallPlaneSideRoomsFromFaceNodes,
  wallConditionFromWallPlaneSides,
} from "../elements/walls2/wallConditionTools";
import { findWallPlaneSubdomainAtPoint } from "../elements/walls2/wallPlaneTrimTools";

/**
 * Fraction along a member length
 *
 * */
const MEMBER_SAMPLE_FRACTION_FROM_LOW_END = 0.25;

export function buildOpeningSourceByOpeningId(
  windowPlacements: Table<GeneratedWindowPlacement>,
  doorPlacements: Table<GeneratedDoorPlacement>,
): Map<string, OpeningSource> {
  const byOpeningId = new Map<string, OpeningSource>();

  for (const placement of Object.values(windowPlacements)) {
    const openingId = placement.handle?.id ?? placement.id;
    if (openingId) {
      byOpeningId.set(openingId, OpeningSource.WINDOW);
    }
  }

  for (const placement of Object.values(doorPlacements)) {
    const openingId = placement.handle?.id ?? placement.id;
    if (openingId) {
      byOpeningId.set(openingId, OpeningSource.DOOR);
    }
  }

  return byOpeningId;
}

export function wallPlaneSideRoomsForSubdomains(
  subdomains: readonly WallPlaneSubdomain[],
  buildingGraph: BuildingGraph,
  rooms: Table<Room>,
): WallPlaneSides {
  let negativeSide: WallPlaneSides["negativeSide"] = null;
  let positiveSide: WallPlaneSides["positiveSide"] = null;

  for (const subdomain of subdomains) {
    if (negativeSide !== null && positiveSide !== null) {
      break;
    }
    const sides = resolveWallPlaneSideRoomsFromFaceNodes(subdomain.faceNodeIds, buildingGraph, rooms);
    negativeSide = negativeSide ?? sides.negativeSide;
    positiveSide = positiveSide ?? sides.positiveSide;
  }

  return { negativeSide, positiveSide };
}

export interface MemberAdjacentRoomsArgs {
  readonly member: Framing;
  readonly descriptor: WallPlaneDescriptor;
  readonly buildingPlane: BuildingGraphPlane;
  readonly buildingGraph: BuildingGraph;
  readonly rooms: Table<Room>;
  readonly regionFallback: WallPlaneSides;
}

export function wallPlaneSideRoomsForMember({
  member,
  descriptor,
  buildingPlane,
  buildingGraph,
  rooms,
  regionFallback,
}: MemberAdjacentRoomsArgs): WallPlaneSides {
  const rail = member.rails[0]?.rail;
  if (!rail) {
    return regionFallback;
  }
  const [sx, sy, sz] = polyLine3StartPoint(rail);
  const [ex, ey, ez] = polyLine3EndPoint(rail);
  const startIsBottom = sz <= ez;
  const bx = startIsBottom ? sx : ex;
  const by = startIsBottom ? sy : ey;
  const bz = startIsBottom ? sz : ez;
  const tx = startIsBottom ? ex : sx;
  const ty = startIsBottom ? ey : sy;
  const tz = startIsBottom ? ez : sz;
  const px = bx + MEMBER_SAMPLE_FRACTION_FROM_LOW_END * (tx - bx);
  const py = by + MEMBER_SAMPLE_FRACTION_FROM_LOW_END * (ty - by);
  const pz = bz + MEMBER_SAMPLE_FRACTION_FROM_LOW_END * (tz - bz);
  const [u, v] = planeProject(buildingPlane.planeFace.plane, px, py, pz);
  const subdomain = findWallPlaneSubdomainAtPoint(descriptor, u, v);
  const sides: WallPlaneSides = subdomain
    ? resolveWallPlaneSideRoomsFromFaceNodes(subdomain.faceNodeIds, buildingGraph, rooms)
    : { negativeSide: null, positiveSide: null };

  return {
    negativeSide: sides.negativeSide ?? regionFallback.negativeSide,
    positiveSide: sides.positiveSide ?? regionFallback.positiveSide,
  };
}

interface ResolveWallFramingMetaInput {
  readonly member: Framing;
  /**
   * Flanking rooms resolved from the wall-plane subdomains. For opening members this is the
   * per-opening value derived from the solid subdomains bordering the opening; otherwise it is the
   * member's own sampled subdomain (with a region-level fallback).
   */
  readonly sides: WallPlaneSides;
  readonly wallHeight: number;
  readonly spacing: number;
  readonly resolvedFramingByOpeningId: ResolvedOpeningFramingTable;
  readonly openingSourceByOpeningId: ReadonlyMap<string, OpeningSource>;
}

export function resolveWallFramingMeta({
  member,
  sides,
  wallHeight,
  spacing,
  resolvedFramingByOpeningId,
  openingSourceByOpeningId,
}: ResolveWallFramingMetaInput): WallFramingMeta {
  const openingIds = member.meta?.openingIds ?? [];
  const primaryOpeningId = openingIds[0];
  const source = primaryOpeningId !== undefined ? openingSourceByOpeningId.get(primaryOpeningId) : undefined;
  const resolved = primaryOpeningId !== undefined ? resolvedFramingByOpeningId[primaryOpeningId] : undefined;

  return {
    adjacentRooms: adjacentRoomHandlesFromWallPlaneSides(sides),
    wallCondition: wallConditionFromWallPlaneSides(sides),
    tag: undefined,
    wallHeight,
    spacing,
    source,
    openingHandle: primaryOpeningId !== undefined ? { type: "OPENING", id: primaryOpeningId } : undefined,
    headerAssemblyId: resolved?.headerAssemblyId,
    headerAssemblyName: resolved?.headerAssemblyName,
    resolvedOpeningFraming: resolved,
  };
}
